import { Link } from 'react-router-dom';
import { CheckCircle, Award, Users, Shield, Star } from 'lucide-react';

// Import images from src/assets/images
import heroImage from '../assets/images/hero-luxury-suburban.webp';
import suburbanInteriorImage from '../assets/images/suburban-interior.webp';
import chauffeurClientImage from '../assets/images/chauffeur-client.webp';
import chauffeurTeamImage from '../assets/images/chauffeur-team.webp';
import chauffeurTechImage from '../assets/images/chauffeur-tech.webp';
import chauffeurDetailImage from '../assets/images/chauffeur-detail.webp';

export default function About() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <h1 className="text-5xl md:text-6xl font-playfair font-bold text-deep-black mb-6 drop-shadow-md">
                About <span className="text-luxury-gold">Pinnacle premier</span> Limo
              </h1>
              <p className="text-lg text-charcoal mb-6 leading-relaxed">
                For over a decade, Pinnacle premier Limo has been the premier choice for luxury transportation 
                services. We specialize in providing exceptional limousine service with our meticulously 
                maintained fleet of luxury vehicles.
              </p>
              <p className="text-lg text-charcoal mb-8 leading-relaxed">
                Our commitment to excellence, punctuality, and customer satisfaction has made us the trusted 
                partner for corporate executives, celebrities, and discerning individuals who demand the very 
                best in professional transportation services.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/services">
                  <button className="bg-luxury-gold text-deep-black rounded hover:bg-yellow-600 font-semibold px-6 py-3 flex items-center hover:scale-105 transition-transform duration-300">
                    <Award className="mr-2 h-5 w-5" />
                    View Our Services
                  </button>
                </Link>
                <Link to="/contact">
                  <button className="border-2  rounded border-luxury-gold text-luxury-gold hover:bg-luxury-gold hover:text-deep-black font-semibold px-6 py-3 flex items-center hover:scale-105 transition-transform duration-300">
                    <Star className="mr-2 h-5 w-5" />
                    Contact Us
                  </button>
                </Link>
              </div>
            </div>
            <div className="relative group">
              <img 
                src={heroImage}
                alt="Luxury Suburban exterior" 
                className="rounded-xl shadow-lg w-full h-auto group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section className="py-20 bg-gradient-to-r from-gray-900 to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { value: "10+", label: "Years of Excellence" },
              { value: "500+", label: "Satisfied Clients" },
              { value: "24/7", label: "Available Service" },
              { value: "100%", label: "Safety Record" },
            ].map((stat, index) => (
              <div 
                key={index} 
                className="text-center p-6 bg-gray-800/50 rounded-lg hover:scale-105 transition-transform duration-300 backdrop-blur-sm"
              >
                <div className="text-4xl font-bold text-luxury-gold mb-2">{stat.value}</div>
                <div className="text-gray-300 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
          </div>
        </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative group">
              <img 
                src={suburbanInteriorImage}
                alt="Luxury Suburban interior" 
                className="rounded-xl shadow-lg w-full h-auto group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-xl"></div>
            </div>
            <div>
              <h2 className="text-4xl font-playfair font-bold text-white mb-8">
                Why Choose <span className="text-luxury-gold">Pinnacle premier?</span>
              </h2>
              <div className="space-y-6">
                {[
                  { title: "Professional Chauffeurs", desc: "Our uniformed, professionally trained chauffeurs provide courteous and discreet service." },
                  { title: "Immaculate Vehicles", desc: "Our fleet is meticulously maintained and regularly inspected for your safety and comfort." },
                  { title: "Flexible Scheduling", desc: "We accommodate your schedule with flexible booking options and route planning." },
                  { title: "Transparent Pricing", desc: "No hidden fees or surprises - our competitive pricing is always upfront and honest." },
                  { title: "Comprehensive Insurance", desc: "Full insurance coverage gives you peace of mind during every journey." },
                ].map((item, index) => (
                  <div 
                    key={index} 
                    className="flex items-start space-x-4 group hover:border-l-4 hover:border-luxury-gold hover:pl-2 transition-all duration-300"
                  >
                    <CheckCircle className="text-luxury-gold h-6 w-6 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-lg mb-2 text-white">{item.title}</h3>
                      <p className="text-gray-300">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section 
        className="py-20 bg-gray-100" 
        style={{ backgroundImage: 'url("https://www.transparenttextures.com/patterns/leather.png")', backgroundSize: '200px' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-playfair font-bold text-deep-black mb-6">
              Our <span className="text-luxury-gold">Mission</span>
            </h2>
            <p className="text-xl text-charcoal max-w-3xl mx-auto">
              To provide unparalleled luxury transportation services that exceed expectations through 
              exceptional service, reliability, and attention to detail.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Users, title: "Customer First", desc: "Every decision we make is centered around providing the best possible experience for our clients." },
              { icon: Shield, title: "Safety & Reliability", desc: "We maintain the highest safety standards and ensure reliable, punctual service every time." },
              { icon: Star, title: "Excellence", desc: "We strive for excellence in every aspect of our service, from our vehicles to our staff." },
            ].map((item, index) => (
              <div 
                key={index} 
                className="text-center p-8 rounded-lg bg-white shadow-lg hover:shadow-xl hover:-translate-y-2 transition-all duration-300"
              >
                <item.icon className="text-luxury-gold h-12 w-12 mx-auto mb-4" />
                <h3 className="text-xl font-playfair font-semibold mb-4 text-deep-black">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-playfair font-bold text-white mb-6 relative inline-block">
              Our <span className="text-luxury-gold">Team</span>
              <span className="absolute bottom-0 left-0 w-full h-1 bg-luxury-gold"></span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Meet the dedicated professionals who make Pinnacle premier Limo the premier choice for luxury transportation.
            </p>
          </div>

          <div className="bg-gray-800 p-8 rounded-xl animate-fade-in-up">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-playfair font-semibold mb-4 text-luxury-gold">
                  Professional Chauffeur Team
                </h3>
                <p className="text-gray-300 mb-6">
                  Our chauffeurs undergo rigorous training and background checks to ensure they meet our high standards 
                  for professionalism, safety, and customer service. Each team member is committed to providing you 
                  with a seamless and luxurious transportation experience.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { value: "10+", label: "Years Average Experience" },
                    { value: "100%", label: "Background Checked" },
                    { value: "24/7", label: "Training Program" },
                    { value: "A+", label: "Customer Rating" },
                  ].map((stat, index) => (
                    <div key={index}>
                      <div className="text-2xl font-bold text-luxury-gold">{stat.value}</div>
                      <div className="text-sm text-gray-300">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { src: chauffeurClientImage, alt: "Chauffeur with client", rotation: "-rotate-2" },
                  { src: chauffeurTeamImage, alt: "Chauffeur team", rotation: "rotate-2" },
                  { src: chauffeurTechImage, alt: "Chauffeur with technology", rotation: "-rotate-3" },
                  { src: chauffeurDetailImage, alt: "Chauffeur detail", rotation: "rotate-3" },
                ].map((img, index) => (
                  <div key={index} className="relative group">
                    <img 
                      src={img.src}
                      alt={img.alt}
                      className={`rounded-lg w-full h-32 object-cover shadow-md ${img.rotation} group-hover:scale-105 transition-transform duration-300 bg-white p-2`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-b from-luxury-gold to-yellow-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-playfair font-bold text-white mb-6 drop-shadow-md">
            Ready to Experience <span className="text-deep-black">Elite Service?</span>
          </h2>
          <p className="text-xl text-white max-w-3xl mx-auto mb-8">
            Contact us today to book your luxury transportation and discover why Pinnacle premier Limo 
            is the preferred choice for discerning clients.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <button className="bg-deep-black rounded text-luxury-gold hover:bg-gray-800 font-semibold px-8 py-4 text-lg animate-pulse">
                Get a Quote
              </button>
            </Link>
            <Link to="/services">
              <button className="border-2  rounded border-white text-white hover:bg-white hover:text-deep-black font-semibold px-8 py-4 text-lg">
                View Services
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}